import { type Feature } from 'geojson';
import { defineStore } from 'pinia';

import {
  getPriceDistribution,
  getPriceEvolution,
  getPriceLevels,
  getPriceRoomDistribution,
} from '@/api/fetchData';
import { isPlatformIs24 } from '@/helpers/platform';
import { propertyTypeToValue } from '@/helpers/propertyTypeMapping';
import {
  type Culture,
  type LevelIdValue,
  LevelValue,
  PropertyType,
} from '@/models/iazi/common/enums';
import {
  type PriceDistributionData,
  type PriceRange,
} from '@/models/iazi/queries/getPriceDistributionResponse';
import { type Data, type PriceDistributions } from '@/stores/types';
import { EventSource, MapGeoLevel, ViewMode } from '@/types';
import { sortLegendColors } from '@/utils/functions';

interface MapState {
  eventSource: EventSource;
  isMapLoading: boolean;
  isDataLoading: boolean;
  currentLevel: MapGeoLevel;
  propertyType: PropertyType;
  viewMode: ViewMode; // only used for mobile
  isDataLayerExpanded: boolean; // only used on mobile devices on the Homegate
  data: Data;
  priceDistributions: PriceDistributions; // TODO: make them base on propertyType next to reduce the api calls
  polygon: {
    current: Feature | null;
    previous: Feature | null;
  };
  // TODO refactor priceDistributions and Legend
  range: PriceRange | null;
  selectedPriceDistributions: PriceDistributions;
  legendColors: Array<string> | null;
  selectedFilterRange: number;
}

export const defaultData: Data = {
  price: null,
  priceRoom: null,
  priceEvoData: null,
};

const useMapStore = defineStore('mapStore', {
  state: (): MapState => ({
    eventSource: EventSource.Init,
    isMapLoading: true,
    isDataLoading: false,
    currentLevel: MapGeoLevel.Country,
    propertyType: PropertyType.House,
    viewMode: isPlatformIs24() ? ViewMode.Data : ViewMode.Map,
    isDataLayerExpanded: false,
    data: defaultData,
    polygon: {
      current: null,
      previous: null,
    },
    priceDistributions: {
      [MapGeoLevel.Canton]: null,
      [MapGeoLevel.Municipality]: null,
      [MapGeoLevel.Town]: null,
    },
    range: null,
    selectedPriceDistributions: {
      [MapGeoLevel.Canton]: null,
      [MapGeoLevel.Municipality]: null,
      [MapGeoLevel.Town]: null,
    },
    legendColors: null,
    selectedFilterRange: 0,
  }),
  actions: {
    setSelectedFilterRange(value: number): void {
      this.selectedFilterRange = value;
    },
    setDataRange(range: PriceRange): void {
      this.range = range;
    },
    setLegendColors(colors: Array<string>): void {
      this.legendColors = colors;
    },
    setEventSource(eventSource: EventSource): void {
      this.eventSource = eventSource;
    },
    setIsMapLoading(isLoading: boolean): void {
      this.isMapLoading = isLoading;
    },
    setIsDataLoading(isLoading: boolean): void {
      this.isDataLoading = isLoading;
    },
    setLoading(isLoading: boolean): void {
      this.setIsMapLoading(isLoading);
      this.setIsDataLoading(isLoading);
    },
    setCurrentLevel(currentLevel: MapGeoLevel): void {
      this.currentLevel = currentLevel;
    },
    setPropertyType(propertyType: PropertyType): void {
      this.propertyType = propertyType;
    },
    setViewMode(viewMode: ViewMode): void {
      this.viewMode = viewMode;
    },
    toggleDataLayer(): void {
      this.isDataLayerExpanded = !this.isDataLayerExpanded;
    },
    setData(data: Data): void {
      this.data = data;
    },
    setPolygon(polygon: { current?: Feature | null; previous?: Feature | null }): void {
      this.polygon = {
        ...this.polygon,
        ...polygon,
      };
    },
    setSelectedPriceDistributions(
      level: MapGeoLevel,
      distributionData: PriceDistributionData,
    ): void {
      if (level == MapGeoLevel.Canton) {
        this.selectedPriceDistributions.canton = distributionData;
      } else if (level == MapGeoLevel.Municipality) {
        this.selectedPriceDistributions.municipality = distributionData;
      } else if (level == MapGeoLevel.Town) {
        this.selectedPriceDistributions.ziptown = distributionData;
      }
    },
    async fetchKpiData({
      level,
      levelId,
      culture,
    }: {
      level: LevelValue;
      levelId: LevelIdValue;
      culture: Culture;
    }): Promise<void> {
      const category = propertyTypeToValue[this.propertyType];

      const [priceRoom, priceEvoData, price] = await Promise.all([
        getPriceRoomDistribution({ level, category, levelId, culture }),
        getPriceEvolution({ level, levelId, category, culture, yearRange: 4 }),
        getPriceLevels({ level, levelId, culture }),
      ]);

      this.setData({
        price,
        priceRoom,
        priceEvoData,
      });
    },
    async fetchPriceDistributions(propertyType: PropertyType, culture: Culture): Promise<void> {
      const propertyTypeValue = propertyTypeToValue[propertyType];
      const [cantonData, municipalityData, townData] = await Promise.all([
        getPriceDistribution({
          level: LevelValue.Canton,
          category: propertyTypeValue,
          culture,
        }),
        getPriceDistribution({
          level: LevelValue.Municipality,
          category: propertyTypeValue,
          culture,
        }),
        getPriceDistribution({
          level: LevelValue.Town,
          category: propertyTypeValue,
          culture,
        }),
      ]);
      if (this.currentLevel == MapGeoLevel.Country && cantonData?.results) {
        this.range = cantonData?.results.range;
        this.legendColors = sortLegendColors(cantonData?.results.colorValues);
      }
      this.priceDistributions = {
        [MapGeoLevel.Canton]: cantonData?.results ?? null,
        [MapGeoLevel.Municipality]: municipalityData?.results ?? null,
        [MapGeoLevel.Town]: townData?.results ?? null,
      };
    },
  },
  getters: {
    isLoading(): boolean {
      return this.isMapLoading && this.isDataLoading;
    },
  },
});

export default useMapStore;
